import type { ChatUser, ChatUserOperationInput } from '@choco-admin/types';
import { useCallback } from 'react';

import { useChatUpdateDataMutationMutation } from '../../../generated';

import { useChatUserOperations } from './useChatUserOperations';

export interface UseUpdateChatBuyerConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Raised when the buyer switch itself committed on the server but the
 * follow-up add-users step failed. This is a PARTIAL success, not a rollback:
 * the chat already points at the new buyer (with every user cleared), so the
 * caller must still reflect the new buyer + the empty, server-known user list
 * and tell the operator to re-add users from the participants list — never
 * report the switch as failed and leave the stale buyer on screen.
 */
export class BuyerReplacedUsersNotAddedError extends Error {
  readonly buyerSwitched = true;

  constructor(message: string) {
    super(message);
    this.name = 'BuyerReplacedUsersNotAddedError';
  }
}

export interface UpdateChatBuyerParams {
  chatId: string;
  buyerId: string;
  /**
   * The buyer users to add after the switch. The caller selects this set
   * explicitly — this hook never expands it to "every user of the new buyer".
   */
  buyerUserIds: string[];
}

export const useUpdateChatBuyer = (config: UseUpdateChatBuyerConfig = {}) => {
  const { onSuccess, onError } = config;

  const [updateChat, { loading: isUpdating }] =
    useChatUpdateDataMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  // Reuse the shared add-users operation so the reconciled list carries the
  // same server-true shape (including the bot flag) as the add/remove flows.
  const { addUsers, isLoading: isAddingUsers } = useChatUserOperations();

  const updateChatBuyer = useCallback(
    async ({
      chatId,
      buyerId,
      buyerUserIds,
    }: UpdateChatBuyerParams): Promise<ChatUser[]> => {
      try {
        // Switch the buyer. The backend clears every user (buyer & distributor)
        // from the chat as a side effect of the change.
        const result = await updateChat({
          variables: {
            id: chatId,
            chat: { buyerId },
          },
        });

        // errorPolicy 'all' resolves even on partial GraphQL failures — surface
        // them so we never report a half-applied buyer switch as success.
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors.map((err) => err.message).join('; '));
        }
        if (!result.data?.chatUpdate) {
          throw new Error('Buyer update returned no chat data');
        }

        // The buyer switch has committed. Explicitly add only the selected
        // buyer users. `addUsers` returns the chat's full, server-true user
        // list, which the caller writes back so the participant UI reflects
        // server truth immediately. With no selection nothing is re-added, so
        // the participant lists are empty.
        const operations: ChatUserOperationInput[] = buyerUserIds.map(
          (userId) => ({
            chatId,
            userId,
            userType: 'buyer',
            operation: 'add',
          })
        );

        let reconciledUsers: ChatUser[];
        try {
          reconciledUsers =
            operations.length > 0 ? await addUsers(operations) : [];
        } catch (addUsersError) {
          // The buyer switch already succeeded server-side (it cleared all
          // users); only the follow-up add failed. Surface a partial-success
          // error so the caller still adopts the new buyer + the empty,
          // server-known user list instead of keeping the old buyer, and tells
          // the operator the switch happened but users must be re-added.
          const detail =
            addUsersError instanceof Error
              ? addUsersError.message
              : 'unknown error';
          throw new BuyerReplacedUsersNotAddedError(
            `The buyer was changed, but the selected users could not be added (${detail}). Re-add them from the participants list.`
          );
        }

        onSuccess?.();
        return reconciledUsers;
      } catch (error) {
        const buyerError =
          error instanceof Error
            ? error
            : new Error('Failed to update chat buyer');
        onError?.(buyerError);
        throw buyerError;
      }
    },
    [updateChat, addUsers, onSuccess, onError]
  );

  return {
    updateChatBuyer,
    isUpdating: isUpdating || isAddingUsers,
  };
};
